import React, { useState } from 'react';
import { useModal } from 'react-modal-hook';
import dayjs from 'dayjs';
import range from 'lodash/range';
import { Cell, Empty, HeaderCell, HeaderRow, ModalFactory, PageItemsExpanded, Pagination, PaginationFooter, PaginationItem, ResultsPerPage, Row, SortLabel, Table, TableBody, TableHead } from 'components';
import { PayoutDetails } from 'modals';
import { IPayout, PayoutStatus } from 'types';
import { centsToDollarsDisplay, centsToNegativeDollarsDisplay } from 'utils/currency';
import { UserPayoutRow } from './components';

interface IPayrollTipsUserDetailPayoutsProps {
  isLoading?: boolean;
  payouts: IPayout[] | null;
  totalRecords?: number;
  recordsPerPage: number;
  setRecordsPerPage: (newRecordsPerPage: number) => void;
  offset: number;
  setOffset: (offset: number) => void;
  sort: string;
  setSort: (field: string) => void;
  appearance: 'default' | 'noContainer';
  onUpdate: () => void;
}

export const PayrollTipsUserDetailPayouts = ({ payouts, isLoading, recordsPerPage, setRecordsPerPage, offset, setOffset, sort, setSort, totalRecords, onUpdate }: IPayrollTipsUserDetailPayoutsProps) => {
  const pages = totalRecords && Math.ceil(totalRecords / recordsPerPage);
  const pagesLimit = 3;
  const activePage = (recordsPerPage > 0 && offset / recordsPerPage + 1) || 1;
  const paginationExpandedItemsLimit = 7;
  const onPageChange = (page: number) => {
    setOffset(page * recordsPerPage);
  };
  const pageItems = pages && pages > 0 && range(pages).map((n) => <PaginationItem key={n} label={(n + 1).toString()} onClick={() => onPageChange(n)} isActive={activePage === n + 1} data-cy="pagination-item" data-value={n + 1} />);

  const isSorted = (headerValue: string) => sort === headerValue || sort === `-${headerValue}`;

  const onSortChange = (newSort: string) => {
    setSort(sort === newSort ? `-${sort}` : newSort);
  };

  const isSortDesc = sort?.[0] === '-';
  const [currentModalPayout, setCurrentModalPayout] = useState<IPayout | null>(null);

  const [showModal, hideModal] = useModal(
    () => (
      <ModalFactory customModal onClose={hideModal} isActive>
        <PayoutDetails payout={currentModalPayout} onClose={hideModal} onUpdate={onUpdate} />
      </ModalFactory>
    ),
    [currentModalPayout, onUpdate]
  );

  return (
    <>
      <Table scrollable sticky>
        {payouts && payouts.length >= 1 && (
          <TableHead>
            <HeaderRow sticky>
              <HeaderCell align="left" onClick={() => onSortChange('payment_date')} data-cy="header-payment-date">
                <SortLabel label="Business Day" isSorted={isSorted('payment_date')} isSortedDesc={isSortDesc} isLoading={isLoading} />
              </HeaderCell>

              <HeaderCell align="left" data-cy="header-payment-status">
                {isLoading ? '' : 'Status'}
              </HeaderCell>

              <HeaderCell align="left" data-cy="header-tips">
                {!isLoading && 'Tips'}
              </HeaderCell>

              <HeaderCell align="left" onClick={() => onSortChange('autoGratuity')} data-cy="header-autograts">
                <SortLabel label="Autograts" isSorted={isSorted('autoGratuity')} isSortedDesc={isSortDesc} isLoading={isLoading} />
              </HeaderCell>

              <HeaderCell align="left" onClick={() => onSortChange('gross')} data-cy="header-gross">
                <SortLabel label="Gross" isSorted={isSorted('gross')} isSortedDesc={isSortDesc} isLoading={isLoading} />
              </HeaderCell>

              <HeaderCell align="left" onClick={() => onSortChange('holdback')} data-cy="header-holdback">
                <SortLabel label="Holdback" isSorted={isSorted('holdback')} isSortedDesc={isSortDesc} isLoading={isLoading} />
              </HeaderCell>

              <HeaderCell align="left" onClick={() => onSortChange('net')} data-cy="header-net">
                <SortLabel label="Net" isSorted={isSorted('net')} isSortedDesc={isSortDesc} isLoading={isLoading} />
              </HeaderCell>
            </HeaderRow>
          </TableHead>
        )}

        <TableBody>
          {!isLoading && payouts && payouts.length === 0 && (
            <Row>
              <Cell colSpan={6}>
                <Empty container containerHeight={300} icon="Payment" message="No results for selected payroll period." />
              </Cell>
            </Row>
          )}

          {isLoading
            ? [...Array(recordsPerPage)].map((e, i) => (
                <UserPayoutRow
                  key={`payouts-loading-${i}`}
                  gross=""
                  holdback=""
                  net=""
                  tips=""
                  autograts=""
                  businessDay=""
                  dateSubmitted=""
                  isLoading
                  payoutId=""
                  sentBy=""
                  site=""
                  timeSubmitted=""
                  onClick={() => null}
                  status={PayoutStatus.PENDING}
                />
              ))
            : payouts?.map((payout: IPayout) => {
                const { status } = payout;
                const createdBy = (payout.statusHistory || []).find((status) => status.action === 'create')?.changedBy?.name || '';

                return (
                  <UserPayoutRow
                    key={`payout-${payout.id}`}
                    alert={payout.alert}
                    gross={centsToDollarsDisplay(payout.gross)}
                    holdback={centsToNegativeDollarsDisplay(payout.holdback)}
                    net={centsToDollarsDisplay(payout.net)}
                    autograts={centsToDollarsDisplay(payout.autoGratuity)}
                    tips={centsToDollarsDisplay(payout.gross - payout.autoGratuity)}
                    businessDay={dayjs(payout.paymentDate).format('MM-DD-YYYY')}
                    dateSubmitted={dayjs(payout.createdAt).format('YYYY-MM-DD')}
                    isLoading={isLoading}
                    data-cy="payout-row"
                    data-value={payout.id}
                    onClick={() => {
                      setCurrentModalPayout(payout);
                      showModal();
                    }}
                    payoutId={payout.id}
                    sentBy={createdBy}
                    site={payout.site.name}
                    status={status}
                    timeSubmitted={dayjs(payout.createdAt).format('hh:mm A z')}
                  />
                );
              })}
        </TableBody>
      </Table>

      <PaginationFooter>
        {pages && pages > 0 ? (
          <>
            <ResultsPerPage
              pageSize={recordsPerPage}
              onChange={(newPerPage: number) => {
                const defaultOffset = 0;
                setRecordsPerPage(newPerPage);
                setOffset(defaultOffset);
              }}
            />
            <Pagination>
              <PaginationItem
                previous
                disabled={activePage === 1}
                onClick={() => {
                  onPageChange(activePage - 2);
                }}
              />
              {pages && pages >= paginationExpandedItemsLimit ? <PageItemsExpanded activePage={activePage} totalPages={pages} limit={pagesLimit} onPageChange={onPageChange} /> : pageItems}
              <PaginationItem
                next
                disabled={activePage === pages}
                onClick={() => {
                  onPageChange(activePage);
                }}
              />
            </Pagination>
          </>
        ) : null}
      </PaginationFooter>
    </>
  );
};
